import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { LoginContext } from "./LoginContext";
import { buscarMinhasPermissoes } from "../../services/auth.service";
import { PermissoesChaves, Token } from "../../utils/localStorage.util";

/**
 * Chaves que liberam **entrar** nas telas de gerenciamento (rota /cadastros/usuarios, etc.).
 * Inclui `read-users`: usuário só com esta chave **vê a lista**, mas **não** cria, edita nem desativa
 * (isso exige `CHAVES_CRIAR_USUARIO` / `CHAVES_ATUALIZAR_USUARIO`).
 */
export const CHAVES_GERENCIAMENTO_USUARIOS = [
  "read-users",
  "create-users",
  "edit-users",
  "update-users",
];

/**
 * Catálogo: permissão “Visualizar usuários” — somente leitura na UI (lista), sem mutação.
 * Uso: documentação e futuros filtros de menu; o `pode()` para ações usa CRIAR / ATUALIZAR.
 */
export const CHAVES_SOMENTE_VISUALIZAR_USUARIOS = ["read-users"];

/** Visualizar usuários também permite abrir o cadastro em modo consulta. */
export const CHAVES_VISUALIZAR_PERFIL_USUARIO = ["read-users"];

/** POST /gerenciamento/usuarios — botão “Novo usuário” e cadastro. `read-users` sozinho não basta. */
export const CHAVES_CRIAR_USUARIO = ["create-users"];

/**
 * Alterar usuário existente: editar linha, desativar/reativar, redefinir senha, salvar PUT, permissões no modal.
 * Aceita a chave canônica `edit-users` e o legado `update-users`. `read-users` sozinho **não** libera estas ações.
 */
export const CHAVES_ATUALIZAR_USUARIO = ["edit-users", "update-users"];

/**
 * Tela Grupos (papéis): /cadastros/grupos-usuarios. Apenas chaves *-roles (sem *-users).
 */
export const CHAVES_ROTA_GRUPOS_ACESSO = [
  "read-roles",
  "create-roles",
  "update-roles",
  "delete-roles",
];

/** POST /gerenciamento/grupos */
export const CHAVES_CRIAR_GRUPO = ["create-roles"];

/** PUT /gerenciamento/grupos/:id */
export const CHAVES_EDITAR_GRUPO = ["update-roles"];

/** Duplicar grupo — exige criar + editar (cria uma cópia com as mesmas permissões). */
export const CHAVES_DUPLICAR_GRUPO = ["create-roles", "update-roles"];

/** Ativar/desativar grupo */
export const CHAVES_ALTERAR_STATUS_GRUPO = ["update-roles"];

/** Excluir grupo */
export const CHAVES_EXCLUIR_GRUPO = ["delete-roles"];

/** Rota /acessos/funcionalidades — mesmo critério de mutação de cadastros administrativos. */
export const CHAVES_ROTA_FUNCIONALIDADES = ["create-users", "update-users"];

/** Cadastros → Empresas (`/cadastros/empresas`). */
export const CHAVES_ROTA_EMPRESAS = [
  "read-companies",
  "create-companies",
  "update-companies",
];
export const CHAVES_EMPRESAS_LEITURA = ["read-companies"];
export const CHAVES_EMPRESAS_CRIAR = ["create-companies"];
export const CHAVES_EMPRESAS_EDITAR = ["update-companies"];

/** Cadastros → Grupos Econômicos (`/cadastros/grupos-economicos`). */
export const CHAVES_ROTA_GRUPOS_ECONOMICOS = [
  "read-economic-groups",
  "create-economic-groups",
  "update-economic-groups",
];
export const CHAVES_GRUPOS_ECONOMICOS_LEITURA = ["read-economic-groups"];
export const CHAVES_GRUPOS_ECONOMICOS_CRIAR = ["create-economic-groups"];
export const CHAVES_GRUPOS_ECONOMICOS_EDITAR = ["update-economic-groups"];

/**
 * Rotas Prévias / Projetos INSS e item do menu com href `/esocial/*`.
 * Qualquer permissão granular INSS deve exibir o menu (não só `inss-view`).
 */
export const CHAVES_ROTA_INSS = [
  "inss-view",
  "inss-create",
  "inss-edit",
  "inss-delete",
  "inss-approve",
  "inss-export",
  "inss-config-view",
  "inss-config-edit",
];

export const CHAVES_INSS_VIEW = ["inss-view"];
export const CHAVES_INSS_CREATE = ["inss-create"];
export const CHAVES_INSS_EDIT = ["inss-edit"];
export const CHAVES_INSS_DELETE = ["inss-delete"];
export const CHAVES_INSS_APPROVE = ["inss-approve"];
export const CHAVES_INSS_EXPORT = ["inss-export"];
export const CHAVES_INSS_CONFIG_VIEW = ["inss-config-view"];
export const CHAVES_INSS_CONFIG_EDIT = ["inss-config-edit"];

/** Rotas `/piscofins/*` e itens de menu PIS/COFINS — qualquer chave granular libera acesso ao módulo. */
export const CHAVES_ROTA_PIS_COFINS = [
  "pis-cofins-view",
  "pis-cofins-edit",
  "pis-cofins-delete",
  "pis-cofins-import",
  "pis-cofins-approve",
  "pis-cofins-export",
  "pis-cofins-config-view",
  "pis-cofins-config-edit",
];

export const CHAVES_PIS_COFINS_VIEW = ["pis-cofins-view"];
export const CHAVES_PIS_COFINS_EDIT = ["pis-cofins-edit"];
export const CHAVES_PIS_COFINS_DELETE = ["pis-cofins-delete"];
export const CHAVES_PIS_COFINS_IMPORT = ["pis-cofins-import"];
export const CHAVES_PIS_COFINS_APPROVE = ["pis-cofins-approve"];
export const CHAVES_PIS_COFINS_EXPORT = ["pis-cofins-export"];
export const CHAVES_PIS_COFINS_CONFIG_VIEW = ["pis-cofins-config-view"];
export const CHAVES_PIS_COFINS_CONFIG_EDIT = ["pis-cofins-config-edit"];

/** Rotas `/importacoes/*` — tela somente leitura com filtros. */
export const CHAVES_ROTA_IMPORTACOES = ["importers-view"];
export const CHAVES_IMPORTACOES_VIEW = ["importers-view"];

/** Automações — `/importacoes/automacoes` (RPA E-Social/SPED, TXM-400). */
export const CHAVES_ROTA_AUTOMACOES = ["automacoes-view", "automacoes-create", "automacoes-edit"];
export const CHAVES_AUTOMACOES_VIEW = ["automacoes-view"];
export const CHAVES_AUTOMACOES_CREATE = ["automacoes-create"];
export const CHAVES_AUTOMACOES_EDIT = ["automacoes-edit"];

/** Conta Corrente — `/conta-corrente/*` */
export const CHAVES_ROTA_CONTA_CORRENTE = ["iss-view", "iss-edit", "iss-export"];
/** Conta Corrente → Projetos (TXM-558) — `/conta-corrente/projetos` e `/conta-corrente/projetos/:id`. */
export const CHAVES_ROTA_PROJETOS_APURACAO = ["projetos-apuracao-view"];

/** Financeiro — `/financeiro/*` */
export const CHAVES_ROTA_FINANCEIRO = [
  "additional-costs-view", "additional-costs-create", "additional-costs-edit",
  "billing-view", "billing-create", "billing-edit", "billing-export",
  "invoice-values-view", "invoice-values-create", "invoice-values-edit",
];

/** Jurídico — `/juridico/*` */
export const CHAVES_ROTA_JURIDICO = ["clients-view", "clients-create", "clients-edit", "clients-delete", "clients-export"];

/** Exportar PDF do proponente */
export const CHAVES_JURIDICO_EXPORT = ["clients-export"];

/** ICMS — `/icms/*` */
export const CHAVES_ROTA_ICMS = ["icms-view", "icms-edit", "icms-export"];

/** Relatórios — `/relatorios/*` */
export const CHAVES_ROTA_RELATORIOS = [
  "finance-reports-view", "finance-reports-create", "finance-reports-edit",
  "finance-reports-delete", "finance-reports-export",
];

/** Apurações — `/relatorios/apuracoes` */
export const CHAVES_ROTA_APURACOES = [
  "apuracoes-view", "apuracoes-create", "apuracoes-edit", "apuracoes-export",
];

/** Apurações — ações granulares dentro da tela (botões) */
export const CHAVES_APURACOES_CREATE = ["apuracoes-create"];
export const CHAVES_APURACOES_EDIT = ["apuracoes-edit"];
export const CHAVES_APURACOES_EXPORT = ["apuracoes-export"];

/** Processamento de Relatório — `/relatorios/processamento` */
export const CHAVES_ROTA_PROCESSAMENTO = [
  "processamento-view", "processamento-edit",
];

/** Processamento de Relatório — ações granulares dentro da tela (botões) */
export const CHAVES_PROCESSAMENTO_EDIT = ["processamento-edit"];

/** Vínculos — `/vinculos/*` */
export const CHAVES_ROTA_VINCULOS = ["links-view", "links-create", "links-edit", "links-delete"];

/** Workflow — `/workflow/*` */
export const CHAVES_ROTA_WORKFLOW = [
  "workflow-config-view", "workflow-config-create", "workflow-config-edit", "workflow-config-delete",
  "read-solicitacoes", "approve-solicitacoes",
];

/** Parceiros e Associados — `/acessos/parceiros-associados` */
export const CHAVES_ROTA_PARCEIROS = [
  "partners-view",
  "partners-create",
  "partners-edit",
  "partners-delete",
];
export const CHAVES_PARCEIROS_VIEW = ["partners-view"];
export const CHAVES_PARCEIROS_CREATE = ["partners-create"];
export const CHAVES_PARCEIROS_EDIT = ["partners-edit"];
export const CHAVES_PARCEIROS_DELETE = ["partners-delete"];

/** Parametrizações — `/parametrizacoes/*` */
export const CHAVES_ROTA_PARAMETRIZACOES = [
  "edit-general-config", "view-general-config",
  "ip-whitelist-add", "ip-whitelist-delete", "ip-whitelist-export", "ip-whitelist-read",
  "tax-categories-view", "tax-categories-create", "tax-categories-edit", "tax-categories-delete",
  "legal-basis-view", "legal-basis-create", "legal-basis-edit", "legal-basis-delete",
  "conversion-factor-view", "conversion-factor-create", "conversion-factor-edit", "conversion-factor-delete",
  "selic-view", "selic-update",
  "ncm-table-view", "ncm-table-create", "ncm-table-edit", "ncm-table-edit-global",
];
export const CHAVES_SELIC_VIEW = ["selic-view"];
export const CHAVES_SELIC_UPDATE = ["selic-update"];

/** Tabela NCM — `/parametrizacoes/tabela-ncm` (TXM-594), ações granulares dentro da tela. */
export const CHAVES_TABELA_NCM_CREATE = ["ncm-table-create"];
export const CHAVES_TABELA_NCM_EDIT = ["ncm-table-edit"];
/**
 * Gravar Alíquota interna / Método de cálculo em linha global (sem Cliente, vale para todos os
 * clientes). Soma-se a create/edit, não os substitui: os demais campos seguem essas duas chaves.
 */
export const CHAVES_TABELA_NCM_EDIT_GLOBAL = ["ncm-table-edit-global"];

const PermissoesContext = createContext(null);

/** Normaliza itens da API (string ou { chave } / { id }). */
function normalizarListaPermissoes(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => {
      if (typeof x === "string") return x.trim();
      if (x && typeof x === "object") {
        if (typeof x.chave === "string") return x.chave.trim();
        if (typeof x.id === "string") return x.id.trim();
      }
      return null;
    })
    .filter(Boolean);
}

function parseChavesArmazenadas() {
  try {
    const raw = PermissoesChaves.get();
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { chaves: normalizarListaPermissoes(parsed), grupoTecnico: false, superAdmin: false, idsNiveisAssociacao: [] };
    }
    if (parsed && typeof parsed === "object") {
      return {
        chaves: normalizarListaPermissoes(parsed.chaves),
        grupoTecnico: Boolean(parsed.grupoTecnico),
        superAdmin: Boolean(parsed.superAdmin),
        idsNiveisAssociacao: Array.isArray(parsed.idsNiveisAssociacao)
          ? parsed.idsNiveisAssociacao.map(Number).filter((id) => Number.isInteger(id) && id > 0)
          : [],
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function PermissoesProvider({ children }) {
  const { dadosLogin } = useContext(LoginContext);
  const inicial = parseChavesArmazenadas();
  const [chaves, setChaves] = useState(() => inicial?.chaves ?? []);
  const [grupoTecnico, setGrupoTecnico] = useState(() => inicial?.grupoTecnico ?? false);
  const [superAdmin, setSuperAdmin] = useState(() => inicial?.superAdmin ?? false);
  const [idsNiveisAssociacao, setIdsNiveisAssociacao] = useState(
    () => inicial?.idsNiveisAssociacao ?? []
  );
  const [carregado, setCarregado] = useState(() => inicial !== null);
  const [carregando, setCarregando] = useState(false);

  const recarregar = useCallback(async () => {
    const token = Token.getToken();
    if (!token) {
      setChaves([]);
      setGrupoTecnico(false);
      setSuperAdmin(false);
      setIdsNiveisAssociacao([]);
      setCarregado(true);
      PermissoesChaves.remove();
      return;
    }
    setCarregando(true);
    try {
      const res = await buscarMinhasPermissoes();
      const list = normalizarListaPermissoes(res?.permissoes);
      const tech = Boolean(res?.grupoTecnico);
      const niveisIds = Array.isArray(res?.idsNiveisAssociacao)
        ? res.idsNiveisAssociacao.map(Number).filter((id) => Number.isInteger(id) && id > 0)
        : [];
      const isSuperAdmin = Boolean(res?.superAdmin);
      setChaves(list);
      setGrupoTecnico(tech);
      setSuperAdmin(isSuperAdmin);
      setIdsNiveisAssociacao(niveisIds);
      PermissoesChaves.set(JSON.stringify({ chaves: list, grupoTecnico: tech, superAdmin: isSuperAdmin, idsNiveisAssociacao: niveisIds }));
    } catch (e) {
      console.warn("[Permissoes] Falha ao carregar minhas-permissoes:", e?.message || e);
      setChaves([]);
      setGrupoTecnico(false);
      setSuperAdmin(false);
      setIdsNiveisAssociacao([]);
      PermissoesChaves.remove();
    } finally {
      setCarregado(true);
      setCarregando(false);
    }
  }, []);

  const idUsuario = dadosLogin?.idUsuario;
  const temSessaoUsuario =
    idUsuario !== "" && idUsuario !== null && idUsuario !== undefined;

  useEffect(() => {
    if (!temSessaoUsuario) {
      setChaves([]);
      setGrupoTecnico(false);
      setSuperAdmin(false);
      setIdsNiveisAssociacao([]);
      setCarregado(false);
      PermissoesChaves.remove();
      return;
    }
    recarregar();
  }, [temSessaoUsuario, idUsuario, recarregar]);

  /**
   * - Grupo técnico (Developer / Super Admin): igual ao middleware — libera todas as chaves de UI.
   * - Lista vazia (após carregar): nega rotas/ações que exijam chave — alinhado ao middleware /gerenciamento.
   * - Lista com pelo menos uma chave: aplica restrição (ex.: só read-users esconde editar/criar).
   */
  const pode = useCallback(
    (anyOf = []) => {
      if (!Array.isArray(anyOf) || anyOf.length === 0) return true;
      if (grupoTecnico || superAdmin) return true;
      const list = chaves || [];
      if (list.length === 0) return false;
      return anyOf.some((k) => list.includes(k));
    },
    [chaves, grupoTecnico, superAdmin]
  );

  const value = useMemo(
    () => ({
      chaves,
      grupoTecnico,
      superAdmin,
      idsNiveisAssociacao,
      carregado,
      carregando,
      pode,
      recarregar,
    }),
    [chaves, grupoTecnico, superAdmin, idsNiveisAssociacao, carregado, carregando, pode, recarregar]
  );

  return (
    <PermissoesContext.Provider value={value}>
      {children}
    </PermissoesContext.Provider>
  );
}

export function usePermissoes() {
  const ctx = useContext(PermissoesContext);
  if (!ctx) {
    throw new Error("usePermissoes deve ser usado dentro de PermissoesProvider");
  }
  return ctx;
}

/** Esconde trecho da UI se o usuário não tiver nenhuma das chaves (após carregar). */
export function IfPermissao({ anyOf = [], children, fallback = null }) {
  const { pode, carregado } = usePermissoes();
  if (!carregado) return fallback;
  return pode(anyOf) ? children : fallback;
}
